import { useEffect } from "react";

/**
 * useOutsideClick
 *
 * Custom React hook that detects clicks outside both a trigger element
 * (e.g., a button) and a container element (e.g., a dropdown, modal, or popover).
 * When a click occurs outside of both elements, the provided callback is executed.
 *
 * @param {React.RefObject<HTMLElement>} triggerRef - Ref to the element that toggles the container (e.g., button)
 * @param {React.RefObject<HTMLElement>} containerRef - Ref to the element that should close when clicking outside
 * @param {() => void} callback - Function called when a click outside both refs is detected
 *
 * @example
 * const triggerRef = useRef(null);
 * const dropdownRef = useRef(null);
 *
 * useOutsideClick(triggerRef, dropdownRef, () => setIsOpen(false));
 */
export const useOutsideClick = (
    triggerRef: React.RefObject<HTMLElement>,
    containerRef: React.RefObject<HTMLElement>,
    callback: () => void
) => {

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            const target = event.target as Node;
            const isClickOutOfTrigger = triggerRef.current && !triggerRef.current.contains(target);
            const isClickOutOfDropdown = containerRef.current && !containerRef.current.contains(target);

            if (isClickOutOfTrigger && isClickOutOfDropdown) {
                callback();
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [triggerRef, containerRef, callback]);

}